/** Preview of the Material Status an analysis would generate, as returned by the backend. */
export interface MaterialStatusSummary {
  analysis_date: string | null;
  horizon_weeks: number | null;
  included_boms: number | null;
  models_count: number | null;
  items_count: number | null;
  connection_active: boolean | null;
  ready_to_generate: boolean;
  has_material_status?: boolean;
  material_status_uuid?: string | null;
  material_status_url?: string | null;
}

export const EMPTY_MATERIAL_STATUS_SUMMARY: MaterialStatusSummary = {
  analysis_date: null,
  horizon_weeks: null,
  included_boms: null,
  models_count: null,
  items_count: null,
  connection_active: null,
  ready_to_generate: false,
};

export interface MaterialStatusPriorityDto {
  pn: string;
  priority: number;
}

export interface MaterialStatusQuantityDto {
  part_number: string;
  week: string;
  /** DRF serializes DecimalField values as strings. Keep them lossless. */
  quantity: string;
}

export interface MaterialStatusMaterialOnHandDto extends MaterialStatusQuantityDto {
  limiting_component: string | null;
}

/** Frozen local snapshot returned by both GET and POST. */
export interface MaterialStatusSnapshotDto {
  id: number;
  generation_uuid: string;
  analysis: number;
  name: string;
  baseline_date: string;
  weeks: string[];
  priorities: MaterialStatusPriorityDto[];
  stock_captured_at: string | null;
  requested_by: number | null;
  requested_by_name: string | null;
  created_at: string;
  material_status_url: string;
  forecast_net: MaterialStatusQuantityDto[];
  finished_goods_wip: MaterialStatusQuantityDto[];
  material_on_hand: MaterialStatusMaterialOnHandDto[];
}

export type MaterialStatusUiStatus = "idle" | "generating" | "success" | "error";

export type MaterialStatusRowKey =
  | "forecast_net"
  | "forecast_flex"
  | "finished_goods_wip"
  | "material_on_hand"
  | "in_transit"
  | "eta"
  | "po_pending"
  | "total_supply"
  | "projected_net"
  | "projected_flex"
  | "dsi";

export type MaterialStatusRowKind = "forecast" | "supply" | "total" | "projection" | "dsi";

/** `unavailable` marks rows PlanMat has no source for; their values are always null. */
export type MaterialStatusRowSource = "snapshot" | "calculated" | "unavailable";

export type MaterialStatusDsiStatus = "red" | "yellow" | "green" | "blue";

export type MaterialStatusDsiRange = "critical" | "short" | "adequate" | "comfortable";

export interface MaterialStatusGridWeek {
  week: string;
  date: string | null;
  month: string | null;
  is_baseline: boolean;
}

export interface MaterialStatusGridRow {
  key: MaterialStatusRowKey;
  kind: MaterialStatusRowKind;
  source: MaterialStatusRowSource;
  values: Array<number | null>;
  /** Only present on the DSI row. */
  status?: Array<MaterialStatusDsiStatus | null>;
}

export interface MaterialStatusGridItem {
  part_number: string;
  priority: number | null;
  limiting_component: string | null;
  summary: {
    shortage_index: number | null;
    shortage_week: string | null;
    shortage_quantity: number | null;
  };
  rows: MaterialStatusGridRow[];
}

export interface MaterialStatusGridKpis {
  pn_count: number;
  /** PlanMat holds no RAG, so the backend always returns null. */
  red_rag_count: number | null;
  shortage_count: number;
  nearest_shortage_week: string | null;
}

/** Material Status grid calculated by the backend from the frozen snapshot. */
export interface MaterialStatusGridResponse {
  generation: {
    generation_uuid: string;
    name: string;
    baseline_date: string;
    created_at: string;
  };
  weeks: MaterialStatusGridWeek[];
  baseline_index: number;
  baseline_week: string;
  kpis: MaterialStatusGridKpis;
  items: MaterialStatusGridItem[];
}

/** Calculation memory of one part number and week, as returned by the trace endpoint. */
export interface MaterialStatusCellTrace {
  part_number: string;
  week: string;
  week_index: number;
  baseline_week: string;
  is_baseline: boolean;
  projection: {
    previous_week: string | null;
    previous_balance: number | null;
    forecast_net: number | null;
    finished_goods_wip: number | null;
    material_on_hand: number | null;
    total_supply: number | null;
    projected_inventory: number | null;
    missing_quantity: number | null;
  };
  dsi: {
    window: Array<{ week: string; forecast_net: number | null }>;
    is_partial: boolean;
    window_total: number;
    working_days: number;
    daily_average: number | null;
    raw_value: number | null;
    value: number | null;
    status: MaterialStatusDsiStatus | null;
    range: MaterialStatusDsiRange | null;
    unavailable_reason: "before_baseline" | "no_future_demand" | null;
  };
  shortage: {
    first_shortage_week: string | null;
    first_shortage_quantity: number | null;
    recovery_week: string | null;
    weeks_until_recovery: number | null;
  };
  material_on_hand: {
    quantity: number | null;
    limiting_component: string | null;
  } | null;
}

export interface MaterialStatusMaterialOnHandComponentDetail {
  part_number: string;
  description: string | null;
  usage: number | string;
  required: number | string;
  available: number | string;
  missing: number | string;
  covered_kits?: number | string | null;
  is_limiting?: boolean;
}

export interface MaterialStatusMaterialOnHandDetail {
  part_number: string;
  description: string | null;
  week: string;
  week_date: string | null;
  scenario: string;
  kits_to_produce: number | string;
  coverage_kits: number | string;
  missing_kits: number | string;
  limiting_components: string[];
  has_breakdown: boolean;
  components: MaterialStatusMaterialOnHandComponentDetail[];
}

